import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  Home, Baby, Users, BookOpen, Settings, LayoutDashboard,
  Building2, CreditCard, Receipt, FileText, BarChart3,
  TicketCheck, Headset, Shield, Wallet, ClipboardCheck, GraduationCap,
} from "lucide-react";

type Item = { to: string; icon: any; label: string; end?: boolean };

/**
 * شريط تنقل سفلي للموبايل — يظهر فقط على الشاشات < md (768px).
 * يختار العناصر حسب دور المستخدم وموقع المسار الحالي.
 */
export default function MobileBottomNav() {
  const { roles, profile } = useAuth();
  const { pathname } = useLocation();

  // قرر مجموعة العناصر بناءً على المسار الحالي + الدور
  let items: Item[] = [];

  if (pathname.startsWith("/admin")) {
    items = [
      { to: "/admin", icon: LayoutDashboard, label: "الرئيسية", end: true },
      { to: "/admin/nurseries", icon: Building2, label: "الحضانات" },
      { to: "/admin/subscriptions", icon: CreditCard, label: "الاشتراكات" },
      { to: "/admin/users", icon: Users, label: "المستخدمون" },
      { to: "/admin/settings", icon: Settings, label: "الإعدادات" },
    ];
  } else if (pathname.startsWith("/accountant")) {
    items = [
      { to: "/accountant", icon: LayoutDashboard, label: "الرئيسية", end: true },
      { to: "/accountant/invoices", icon: FileText, label: "الفواتير" },
      { to: "/accountant/subscriptions", icon: CreditCard, label: "الاشتراكات" },
      { to: "/accountant/reports", icon: BarChart3, label: "التقارير" },
    ];
  } else if (pathname.startsWith("/support-panel")) {
    items = [
      { to: "/support-panel", icon: LayoutDashboard, label: "الرئيسية", end: true },
      { to: "/support-panel/tickets", icon: TicketCheck, label: "التذاكر" },
      { to: "/support-panel/users", icon: Users, label: "المستخدمون" },
      { to: "/support-panel/stats", icon: BarChart3, label: "الإحصائيات" },
    ];
  } else if (pathname.startsWith("/dashboard")) {
    const isParent = roles.includes("parent") && !roles.includes("admin");
    if (isParent) {
      items = [
        { to: "/dashboard/parent", icon: Home, label: "الرئيسية" },
        { to: "/dashboard/parent/children", icon: Baby, label: "أطفالي" },
        { to: "/dashboard/parent/lessons", icon: BookOpen, label: "الدروس" },
        { to: "/dashboard/parent/payments", icon: Wallet, label: "المدفوعات" },
        { to: "/dashboard/settings", icon: Settings, label: "حسابي" },
      ];
    } else if (roles.includes("teacher") || roles.includes("supervisor")) {
      items = [
        { to: "/dashboard", icon: LayoutDashboard, label: "الرئيسية", end: true },
        { to: "/dashboard/classes", icon: GraduationCap, label: "الفصول" },
        { to: "/dashboard/attendance", icon: ClipboardCheck, label: "الحضور" },
        { to: "/dashboard/lessons", icon: BookOpen, label: "الدروس" },
        { to: "/dashboard/settings", icon: Settings, label: "الإعدادات" },
      ];
    } else {
      // Admin / default
      items = [
        { to: "/dashboard", icon: LayoutDashboard, label: "الرئيسية", end: true },
        { to: "/dashboard/children", icon: Baby, label: "الأطفال" },
        { to: "/dashboard/staff", icon: Users, label: "الموظفون" },
        { to: "/dashboard/finance", icon: Receipt, label: "المالية" },
        { to: "/dashboard/settings", icon: Settings, label: "الإعدادات" },
      ];
    }
  }

  if (items.length === 0) return null;

  return (
    <nav
      role="navigation"
      aria-label="التنقل السفلي"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border shadow-lg"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      dir="rtl"
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 py-2 px-1 text-[11px] font-medium transition-colors",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      "w-5 h-5 transition-transform",
                      isActive && "scale-110"
                    )}
                  />
                  <span className="truncate max-w-full leading-none">{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
